import React, { CSSProperties, useEffect } from 'react';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import { useSettings } from '../../../hooks/useSettings';
import { useTranslations } from '../../../hooks/useTranslations';
import { ExperienceHost } from '../../../../../__generated__/loma';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import NarvarLogo from './components/NarvarLogo/NarvarLogo';
import { useTheme } from '../../../hooks/useTheme';
import styles from './NarvarFooter.module.css';
import { clsx } from 'clsx';

export { NarvarLogo };

const DEFAULT_LOGO_HREF = 'https://corp.narvar.com/survey';

export interface NarvarFooterProps {
  featureName?: string;
  experienceHost?: ExperienceHost;
  // The padding definitions in this component have been around since the beginning:
  // https://github.com/narvar/nth/blob/51b2cf332704264918dbe95ac80f47fd5d7154f5/packages/nth-narvar-footer/src/index.js#L20-#L22
  // We could probably get rid of these different padding settings for different screenwidths and/or
  // only apply padding that only styles the component internally. Any padding that leaks external
  // should be added externally
  hack_paddingOverride?: Pick<CSSProperties, 'padding'>;
}

const NarvarFooter = ({
  featureName,
  experienceHost,
  hack_paddingOverride,
}: NarvarFooterProps) => {
  const { narvarFooter } = useSettings();
  const { logoHref, hideLogo, hideInEmbed, hidePrivacyLink } =
    narvarFooter ?? {};

  const {
    narvarfooterNoneNone,
    linksLegalDefault,
    linksLegalHover,
    iconsFooterNone,
  } = useTheme();

  const { footerPrivacyLinkText, footerPrivacyLink } = useTranslations();

  const logEvent = useAnalyticsPost();

  useEffect(() => {
    logEvent({
      feature: featureName,
      eventComponent: NarvarEventComponents.NTH_BUNDLE_NARVAR_FOOTER,
      eventFeature: NarvarEventComponents.NTH_BUNDLE_NARVAR_FOOTER,
      pageComponent: NarvarEventComponents.NTH_BUNDLE_NARVAR_FOOTER,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
    });
  }, [featureName, logEvent]);

  const LogoBlock = (
    <div className={styles.logoColumn}>
      <a
        href={logoHref || DEFAULT_LOGO_HREF}
        target="_blank"
        rel="noopener noreferrer"
      >
        <NarvarLogo style={iconsFooterNone} />
      </a>
    </div>
  );

  const LinksBlock = (
    <div className={styles.legalColumn}>
      <TextLink
        linkStyle={{
          sDefault: {
            fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
            fontSize: '0.625rem',
            fontWeight: 300,
            lineHeight: '0.75rem',
            letterSpacing: '0.01875rem',
            color: '#999',
            textDecoration: 'none',
            ...linksLegalDefault,
          },
          sHover: {
            fontSize: '0.625rem',
            color: (linksLegalHover && linksLegalHover.color) || '#999',
            ...linksLegalHover,
          },
        }}
        href={footerPrivacyLink || 'https://corp.narvar.com/terms/'}
        target={TextLinkTargetType.BLANK}
        rel="noopener noreferrer"
      >
        {footerPrivacyLinkText}
      </TextLink>
    </div>
  );

  const isEmbed = experienceHost === 'EMBED';

  if (isEmbed && hideInEmbed) return null;

  return (
    <footer role="contentinfo">
      <div
        className={clsx({
          [styles.footer]: true,
          [styles.footerPadding]: !isEmbed,
          [styles.footerPaddingEmbed]: isEmbed,
        })}
        style={{
          ...hack_paddingOverride,
          // TODO: remove #FFF background color from
          // default narvarfooterNoneNone styling
          ...narvarfooterNoneNone,
          backgroundColor: isEmbed
            ? undefined
            : narvarfooterNoneNone?.backgroundColor,
        }}
      >
        {!hideLogo && LogoBlock}
        {!isEmbed && !hidePrivacyLink && LinksBlock}
      </div>
    </footer>
  );
};

export default NarvarFooter;
